<template>
  <div class="reportModal">
    <!-- <div v-show="dropActive" class="reportModal--dropAnywhere">Drop image anywhere</div> -->
    <div class="reportModal--container" ref="dropArea" @dragenter.prevent="initDragOverlay">
      <div
        class="reportModal--dropAnywhere"
        :class="dropActive ? 'reportModal--dropHighlight' : ''"
        @dragenter.prevent="handleDrag"
        @dragover.prevent="handleDrag"
        @dragleave.prevent="handleDragStop"
        @drop.prevent="handleDrop"
      >
        Drop image anywhere
      </div>
      <div v-show="submitSuccess === null" class="reportModal--main" :class="view || selectedOption ? 'reportModal--options-container' : ''">
        <!-- Navigation -->
        <div class="reportModal--nav">
          <div class="reportModal--back" v-show="view || selectedOption" @click="goBack('')">
            <IconBase iconColor="#111" viewBox="0 0 64 64" width="100%" height="100%" strokeWidth="1px">
              <IconLeftArrow />
            </IconBase>
          </div>
          <div class="reportModal--title">Report</div>

          <!-- Default close modal -->
          <div class="reportModal--close" @click="closeModal">
            <IconBase iconColor="#111" viewBox="0 0 329.26933 329" width="100%" height="100%" strokeWidth="1px">
              <IconClose />
            </IconBase>
          </div>
        </div>

        <!-- Section title -->
        <div class="reportModal--select" :class="view && selectedOption ? 'reportModal--submitTitle' : ''">{{ getTitle }}</div>

        <!-- Initial selection -->
        <div class="reportModal--button" v-show="!view" @click="setView('user')">Report user</div>
        <div class="reportModal--button" v-show="!view" @click="setView('bug')">Report bug</div>

        <!-- Category selection -->
        <div class="reportModal--options" v-show="view === 'user' && !selectedOption">
          <div class="reportModal--option" v-for="(o, i) in options" :key="i" @click="setOption(o.option)">{{ o.option }}</div>
        </div>
        <div class="reportModal--options" v-show="view === 'bug' && !selectedOption">
          <div class="reportModal--option" v-for="(o, i) in bugOptions" :key="i" @click="setOption(o.option)">{{ o.option }}</div>
        </div>

        <!-- Submit -->
        <div class="reportModal--submission" v-show="view && selectedOption">
          <div class="reportModal--selectedOption">{{ selectedOption }}</div>
          <div class="reportModal--sectionTitle">Specify details</div>
          <textarea
            class="reportModal--details"
            ref="reportDetails"
            v-model="reportDetails"
            @input="updateInputHeight"
            placeholder="Write anything else you think we should know that could help us better address the report (optional)."
          ></textarea>
          <input type="file" id="reportModal--fileUploadInput" multiple accept="image/*" @change="handleUploadFileEvent" />
          <label class="reportModal--imageUpload" for="reportModal--fileUploadInput" v-show="!images || !images.length">
            <div class="reportModal--imageUploadTitle">Upload screenshot(s)</div>
            <div class="reportModal--imageUploadSub">Or drop a file</div>
          </label>
          <div class="reportModal--sectionTitle" v-show="images && images.length">Upload screenshot(s)</div>
          <div class="reportModal--images" v-show="images && images.length">
            <div v-for="(image, j) in images" :key="j" class="reportModal--imageContainer">
              <!-- Show image from file or file url once upload complete -->
              <img :src="image.file || image.fileUrl" class="reportModal--image" />
              <div class="reportModal--uploadingMeta" v-show="image.uploading">
                <!-- Loader UI -->
                <div class="lds-default reportModal--uploadingMeta">
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
              </div>
              <div class="reportModal--uploadingMeta" v-show="image.uploadFailed">Upload failed</div>
              <div class="reportModal--removeImage" @click="removeImage(j)">
                <IconBase iconColor="#111" viewBox="0 0 329.26933 329" width="100%" height="100%" strokeWidth="1px">
                  <IconClose />
                </IconBase>
              </div>
            </div>
            <input type="file" id="reportModal--fileUploadInput" multiple accept="image/*" @change="handleUploadFileEvent" />
            <label class="reportModal--fileUpload" for="reportModal--fileUploadInput">+</label>
          </div>
          <button class="reportModal--submit" @click="submitReport">
            {{ submittingReport ? "Submitting report..." : "Submit report" }}
          </button>
          <div class="reportModal--discreet" v-show="view === 'user'">All reports will be handled discreetly.</div>
        </div>
      </div>
      <!-- Success / Failure of submission -->
      <div class="reportModal--submitted" v-show="submitSuccess">
        <div class="reportModal--submittedTitle">Thank you for your report!</div>
        <div class="reportModal--submittedSub">We'll be in touch shortly to update you on the status of your report and the actions we'll take.</div>
        <button class="reportModal--submit reportModal--submittedClose" @click="closeModal">Close</button>
      </div>
      <div class="reportModal--submitted" v-show="submitSuccess === false">
        <div class="reportModal--submittedTitle">Something went wrong.</div>
        <div class="reportModal--submittedSub">Please try again or reach out to us directly at support@cofocus.one. Thank you for your patience!</div>
        <button class="reportModal--submit reportModal--submittedClose" @click="closeModal">Close</button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import IconBase from "../IconBase.vue";
import IconClose from "../SVG/IconClose.vue";
import IconLeftArrow from "../SVG/IconLeftArrow.vue";
import { updateInputHeight } from "../../utilities/inputFormatter";
import ImageUploader from "../../utilities/ImageUploader";
import coreAPI from "../../api/coreAPI";
import isMobile from "ismobilejs";

export default {
  name: "ReportModal",
  components: {
    IconBase,
    IconClose,
    IconLeftArrow,
  },
  created() {
    if (this.$route.meta.isSession) this.section = "session";
    if (this.$route.meta.isProfile) this.section = "profile";
    if (this.$route.meta.isCommunity) this.section = "community";
  },
  data() {
    return {
      section: "",

      view: "",
      selectedOption: "",
      reportDetails: "",
      images: [],

      submitSuccess: null,
      submittingReport: false,

      dropActive: false,

      options: [
        { option: "Partner's camera is off" },
        { option: "Partner away with no prior notice" },
        { option: "Partner out of camera frame" },
        { option: "Harassment" },
        { option: "Nudity" },
        { option: "Privacy violation" },
        { option: "Hate speech" },
        { option: "Selling / Spam" },
        { option: "Other" },
      ],

      bugOptions: [{ option: "Chat not working" }, { option: "Camera not working" }, { option: "Microphone not working" }, { option: "Other" }],
    };
  },
  computed: {
    ...mapState({
      ready: (state) => state.auth.ready,
      user: (state) => state.auth.user,
      isAuthenticated: (state) => state.auth.authenticationStatus,
      sessionID: (state) => state.session.sessionID,
      partnerMetadata: (state) => state.session.partnerMetadata,

      preferenceSettingsOpen: (state) => state.cofocusPreferences.preferenceSettingsOpen,
      messengerOpen: (state) => state.messages.modalOpened,
      welcomeVideoOpen: (state) => state.onboarding.welcomeVideoOpen,
      notices: (state) => state.notices.notices,
      helpCenter: (state) => state.modals.helpCenter,
      reportModal: (state) => state.modals.reportModal,
      onboardingComplete: (state) => state.onboarding.onboardingComplete,
      onboardingLoaded: (state) => state.onboarding.onboardingLoaded,

      sessionChat: (state) => state.session.sessionChat,
    }),
    getTitle() {
      if (this.view && !this.selectedOption) return "Please specify the problem";
      else if (this.view && this.selectedOption) return "Chosen problem:";
      else return "Please select a problem";
    },
  },
  methods: {
    async submitReport() {
      /* Prevent report if not authenticated */
      if (!this.isAuthenticated) {
        this.submitSuccess = false;
        return;
      }

      if (this.submittingReport) return; /* Still submitting previous report */

      /* Ensure no image is still in the process of uploading */
      for (var i = 0; i < this.images.length; i++) {
        if (this.images[i].uploading) return alert("Please wait for images to finish uploading.");
        /** @todo In theory, could have a more graceful resolution, where even if images are still uploading,
         * they will still be linked and user would not need to wait for this. They could start the upload,
         * submit and close.
         */
      }

      this.submittingReport = true;

      /* Construct report */
      const report = {
        UID: this.user._id,
        section: this.section,
        type: this.view,
        category: this.selectedOption,
        metadata: {
          userData: navigator && navigator.userAgent ? navigator.userAgent : "",
          timezone: Intl && Intl.DateTimeFormat().resolvedOptions().timeZone ? Intl.DateTimeFormat().resolvedOptions().timeZone : "",
          sessionChat: this.sessionChat ? this.sessionChat : [],
        },
      };
      if (isMobile().phone) report.metadata.device = "phone";
      if (isMobile().tablet) report.metadata.device = "tablet";
      if (this.section === "session" && this.sessionID) report["SID"] = this.sessionID;
      if (this.view === "user" && this.partnerMetadata && this.partnerMetadata.ID) report["targetID"] = this.partnerMetadata.ID;
      if (this.reportDetails) report["details"] = this.reportDetails;
      if (this.images && this.images.length) report["images"] = this.images;

      const result = await coreAPI.createOneUserReport(report);
      this.submittingReport = false;
      if (result && result.success) {
        window.mixpanel.track("Report", {
          reporterID: report.UID,
          reportedID: report.targetID ? report.targetID : "N/A",
          section: report.section,
          type: report.type,
          category: report.category,
          sessionID: report.SID ? report.SID : "N/A",
        });
        this.submitSuccess = true;
        this.resetReport();
      } else if (!result || !result.success) {
        this.submitSuccess = false;
      }
    },
    removeImage(index) {
      const image = this.images[index];
      const fileId = image.fileId;
      this.images.splice(index, 1);
      if (fileId) coreAPI.deleteOneImage(this.user._id, fileId); /* no need for async */
    },

    /* To check whether dragging images should be enabled or not */
    isUploadView() {
      if (this.view && this.selectedOption) return true;
      return false;
    },
    initDragOverlay() {
      if (!this.isUploadView()) return;
      if (!this.dropActive) this.dropActive = true;
    },
    handleDrag() {
      if (!this.isUploadView()) return;
      if (!this.dropActive) this.dropActive = true;
    },
    handleDragStop() {
      if (!this.isUploadView()) return;
      if (this.dropActive) this.dropActive = false;
    },
    async handleDrop(event) {
      if (!this.isUploadView()) return;
      if (this.dropActive) this.dropActive = false;
      const dataTransfer = event.dataTransfer;
      const files = dataTransfer.files;
      await this.handleFiles(files);
    },
    async handleUploadFileEvent(event) {
      const files = event && event.target && event.target.files && event.target.files.length ? event.target.files : null;
      if (files === null) alert("Must provide a valid image file.");
      await this.handleFiles(files);
    },
    async handleFiles(files) {
      files = [...files];
      const uploadArray = [];
      for (var i = 0; i < files.length; i++) {
        const file = files[i];
        const upload = this.previewFile(file);
        uploadArray.push(upload);
      }
      await Promise.all(uploadArray);
    },
    async previewFile(file) {
      /* Ensure valid file & metadata exists */
      if (!file || !file.size) return alert("Invalid file.");

      /* Validate image max size */
      if (!this.isAllowedFileSize(file)) {
        return alert("File size must be smaller than 10MB.");
      }

      /* Ensure FileReader is available */
      if (!window.FileReader) return alert("FileReader API not supported.");

      let reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = async () => {
        if (reader && reader.result) {
          const uploadID = Math.floor(Date.now() + Math.random()); /* Save to later modify uplaoded flag to update 'Uploading...' UI */
          const image = {
            file: reader.result,
            fileName: file.name ? file.name : `Unnamed ${uploadID}`,
            uploadID,
            uploading: true,
          };
          this.images.push(image);

          /* We upload image right away (and also client side directly, not first through the server)
           * rather during submission, so that submission itself is fast. */
          const upload = await ImageUploader.uploadOneImageToImageKit(image.file, image.fileName);
          const imageIndex = this.images.findIndex((img) => img.uploadID === uploadID);
          if (!upload || !upload.fileId || !upload.fileUrl || !upload.fileName) {
            this.images[imageIndex]["uploadFailed"] = true;
          } else {
            /* Prep reference data to be sent to the server for storage */
            this.$set(this.images[imageIndex], "fileId", upload.fileId);
            this.$set(this.images[imageIndex], "fileName", upload.fileName);
            this.$set(this.images[imageIndex], "fileUrl", upload.fileUrl);
            delete this.images[imageIndex].file;
            delete this.images[imageIndex].uploadID;
            delete this.images[imageIndex].uploading;
          }
        } else {
          alert("Image upload failed.");
        }
      };
    },
    isAllowedFileSize(file) {
      const fileSize = file.size / 1024 / 1024; // in MiB
      if (fileSize > 10) return false;
      return true;
    },
    updateInputHeight() {
      updateInputHeight(this.$refs.reportDetails);
    },
    closeModal() {
      this.$store.dispatch("modals/setModalState", { modal: "reportModal", newState: false });
      this.submitSuccess = null;
      this.resetReport();
    },
    setView(view) {
      this.view = view;
    },
    goBack() {
      if (this.selectedOption) this.selectedOption = "";
      else if (this.view) this.view = ""; /* else is important or will go back too many steps */
    },
    setOption(option) {
      this.selectedOption = option;
    },
    resetReport() {
      this.view = "";
      this.selectedOption = "";
      this.images = [];
      this.reportDetails = "";
    },
  },
};
</script>

<style scoped>
@import "./styles/ReportModal.css";
</style>
